
import { Container } from "@/components/ui/container";
import { CheckCircle2 } from "lucide-react";

interface ProcessStepProps {
  number: number;
  title: string;
  description: string;
}

const ProcessStep = ({ number, title, description }: ProcessStepProps) => {
  return (
    <div className="flex items-start gap-5">
      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-edgefi-purple text-white flex items-center justify-center font-bold text-xl">
        {number}
      </div>
      <div>
        <h3 className="text-xl font-semibold mb-2 text-gray-900">{title}</h3>
        <p className="text-gray-600">{description}</p>
      </div>
    </div>
  );
};

export const ProcessSteps = () => {
  const steps = [
    {
      number: 1,
      title: "Assessment",
      description: "We assess your current policies, procedures, and compliance requirements to identify gaps and opportunities."
    },
    {
      number: 2,
      title: "Policy Development",
      description: "Our team creates customized policy documents aligned with your business needs and industry requirements."
    },
    {
      number: 3,
      title: "Procedure Documentation",
      description: "We develop detailed procedures that outline how to implement each policy effectively."
    },
    {
      number: 4,
      title: "Stakeholder Review",
      description: "Key stakeholders review and provide feedback to ensure policies are practical and effective."
    },
    {
      number: 5,
      title: "Implementation Plan",
      description: "We create a rollout plan that includes communication, training, and adoption strategies."
    },
    {
      number: 6,
      title: "Continuous Improvement",
      description: "Regular reviews ensure policies remain effective and adapt to changing business and security landscapes."
    }
  ];

  return (
    <section className="py-16 bg-white">
      <Container>
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4 text-gray-900">Our Policy Development Process</h2>
            <p className="text-lg text-gray-600">
              We follow a structured approach to developing and implementing effective cybersecurity policies and procedures.
            </p>
          </div>
          
          <div className="space-y-8">
            {steps.map((step) => (
              <ProcessStep 
                key={step.number}
                number={step.number}
                title={step.title}
                description={step.description}
              />
            ))}
          </div>
          
          <div className="mt-12 p-6 bg-gray-50 border border-gray-100 rounded-xl">
            <div className="flex items-start gap-4">
              <CheckCircle2 className="w-6 h-6 text-edgefi-purple flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-semibold mb-2 text-gray-900">Continuous Policy Management</h3>
                <p className="text-gray-600">
                  Our approach doesn't end with document creation. We provide ongoing support to help you manage, update, and improve your policies as your organization and the threat landscape evolve.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};
